import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { AUTH_ERRORS } from "~/constants";
import { useAuth } from "~/providers/auth.provider";
import { AuthState, LoginInput } from "~/types/auth";

import { useLoginMutation } from "../api/mutations";
import { loginSchema } from "../schemas";
import { authService } from "../service/auth.service";

interface ErrorWithMessage {
  message: string;
}

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

export const useLogin = () => {
  const navigate = useNavigate();
  const { setAuthState, refetch } = useAuth();
  const { mutateAsync: loginUser, isPending } = useLoginMutation();
  const [error, setError] = useState<string | null>(null);
  const router = useRouterState();
  const currentPath = router.location.pathname;

  const { status, redirect } = useSearch({
    from: currentPath.includes("weight-loss") 
      ? "/(auth)/_auth/weight-loss" 
      : "/(auth)/_auth/urgent-care"
  });

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });


  const onSubmit = async (data: LoginInput) => {
    setAuthState(AuthState.AUTHENTICATING);
    setError(null);

    try {
      const response = await loginUser(data);

      if (response.success) {
        toast.success("Successfully logged in!");
        authService.setToken(response.data.token);
        await refetch();

        let destination = "/dashboard";
        if (currentPath.includes("urgent-care")) {
          destination = "/dashboard/urgent-care";
        } else if (currentPath.includes("weight-loss")) {
          destination = "/dashboard/weight-loss";
        }

        await navigate({
          to: redirect && redirect !== "/" ? redirect : destination,
          replace: true,
        });
        return;
      }

      setAuthState(AuthState.UNAUTHENTICATED);
      // Handle error response
      const errorMessage = isErrorWithMessage(response.error)
        ? response.error.message
        : typeof response.error === 'string'
          ? response.error
          : AUTH_ERRORS.INVALID_CREDENTIALS;

      setError(errorMessage);
      toast(errorMessage, {
        description: "Please check your credentials and try again.",
      });
    } catch (error) {
      console.error("Login error:", error);
      setAuthState(AuthState.UNAUTHENTICATED);
      const errorMessage = AUTH_ERRORS.INVALID_CREDENTIALS;
      setError(errorMessage);
      toast(errorMessage, {
        description: "Please check your credentials and try again.",
      });
    }
  };

  useEffect(() => {
    if (status === "success") {
      toast.success("Registration successful! Please login to continue.");
    }
  }, [status]);

  return {
    form,
    onSubmit,
    isLoading: isPending,
    error,
  };
};
